/**
 * Builds the multi-sheet Excel/Word/PDF export for the Bank Transactions agent.
 *
 * One workbook, sheets are views + one tab per account:
 *   - "Accounts Summary"  — one row per canonical account.
 *   - "All Transactions"  — every (non-duplicate) transaction.
 *   - one sheet per account — that account's transactions, named
 *     "<Institution> - xx<last four digits>".
 *
 * Money is written as real numbers (debits negative) and dates as real Date
 * cells so the practitioner can sort / sum / filter natively in Excel. A
 * document selection (empty = all) scopes which transactions feed every sheet.
 */

import { computeRunningBalances } from "@cvx/jobs/summarization/agents/bankTransactions/balanceCore";
import type { ExportContent, ExportSheet } from "@/utils/export";
import { TYPE_LABELS } from "./badges.helpers";

export interface ExportTxn {
  date: string;
  amount: number;
  direction: "debit" | "credit";
  description: string;
  counterpartyName?: string;
  extractionCounterpartyName?: string;
  reference?: string;
  balanceAfter?: number;
  accountInstitution?: string;
  accountNumber?: string;
  transactionType: string;
  sourceDocumentId?: string;
  sourceDocumentName?: string;
  sourceFileName?: string;
  isDuplicate?: boolean;
}

export interface ExportAccount {
  institution?: string;
  accountNumber?: string;
  accountName?: string;
  cardholderName?: string;
  accountType?: string;
  transactionCount: number;
  totalDebits: number;
  totalCredits: number;
  firstTxnDate?: string;
  lastTxnDate?: string;
  sourceDocumentIds?: string[];
}

const MONTHS: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

/**
 * Parse the various date strings the extractor emits into a real Date so Excel
 * treats it as a date cell. Falls back to the raw string on an unknown format.
 */
export function parseDateCell(raw?: string): Date | string {
  if (!raw) return "";
  const parsed = ((): { y: number; m: number; d: number } | null => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number) as [number, number, number];
      return { y, m: m - 1, d };
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw)) {
      const [dd, mm, yy] = raw.split("/").map(Number) as [
        number,
        number,
        number,
      ];
      return { y: yy, m: mm - 1, d: dd };
    }
    const match = raw.match(/^(\d{1,2})\s+(\w{3,})\s+(\d{4})$/);
    if (!match) return null;
    return {
      d: Number(match[1]),
      m: MONTHS[match[2].toLowerCase().slice(0, 3)] ?? 0,
      y: Number(match[3]),
    };
  })();
  if (!parsed) return raw;
  const date = new Date(parsed.y, parsed.m, parsed.d);
  return Number.isNaN(date.getTime()) ? raw : date;
}

function accountLabel(institution?: string, accountNumber?: string): string {
  return [institution, accountNumber].filter(Boolean).join(" — ") || "";
}

// ---------------------------------------------------------------------------
// Account keying — mirrors the canonical dedup keying in the Bank Transactions
// manifest so transactions group onto the same account the "Accounts Summary"
// sheet lists. Kept local (a small, stable copy) because the manifest is a
// Convex server module and must not be pulled into the browser bundle.
// ---------------------------------------------------------------------------

const INSTITUTION_ALIASES: Record<string, string> = {
  cba: "commonwealth bank",
  commbank: "commonwealth bank",
  "commonwealth bank of australia": "commonwealth bank",
  "comm bank": "commonwealth bank",
  nab: "national australia bank",
  "national australia bank limited": "national australia bank",
  anz: "anz",
  "anz banking group": "anz",
  "australia and new zealand banking group": "anz",
  westpac: "westpac",
  "westpac banking corporation": "westpac",
  "st george": "st george",
  "st george bank": "st george",
  "bank of queensland": "bank of queensland",
  boq: "bank of queensland",
  "macquarie bank": "macquarie bank",
  macquarie: "macquarie bank",
  "bendigo bank": "bendigo and adelaide bank",
  "bendigo and adelaide bank": "bendigo and adelaide bank",
  "adelaide bank": "bendigo and adelaide bank",
  amex: "american express",
  "american express": "american express",
};

function normalizeInstitution(value: string | undefined): string {
  if (!value) return "";
  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9&\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return INSTITUTION_ALIASES[cleaned] ?? cleaned;
}

function normalizeAccountNumber(value: string | undefined): string {
  if (!value) return "";
  const stripped = value.replace(/[^A-Za-z0-9]/g, "").toLowerCase();
  if (!stripped) return "";
  if (/x/i.test(stripped)) {
    const trailingDigits = stripped.replace(/^.*x/i, "");
    return trailingDigits || stripped;
  }
  if (/^\d+$/.test(stripped) && stripped.length >= 12) {
    return stripped.slice(6);
  }
  return stripped;
}

/** Canonical `institution:accountNumber` grouping key. */
function accountGroupKey(institution?: string, accountNumber?: string): string {
  return `${normalizeInstitution(institution)}:${normalizeAccountNumber(accountNumber)}`;
}

/** Last four digits (or characters) of an account number, for the tab name. */
function lastFour(accountNumber?: string): string {
  const digits = (accountNumber ?? "").replace(/[^0-9]/g, "");
  if (digits.length >= 4) return digits.slice(-4);
  const alnum = (accountNumber ?? "").replace(/[^A-Za-z0-9]/g, "");
  return alnum.slice(-4);
}

/** Per-account sheet name: "<Institution> - xx<last four>". */
function accountSheetName(
  institution?: string,
  accountNumber?: string,
): string {
  const bank = (institution ?? "").trim() || "Account";
  const l4 = lastFour(accountNumber);
  return l4 ? `${bank} - xx${l4}` : bank;
}

const TXN_HEADERS = [
  "Date",
  "Account",
  "Description",
  "Counterparty",
  "Category",
  "Debit",
  "Credit",
  "Balance",
  "Reconciliation",
];

/**
 * Build transaction rows with a per-account running balance (credits add,
 * debits subtract), date-sorted. Debits are written as negative numbers so the
 * Balance is a transparent cumulative sum, seeded and reconciled against the
 * statements' own stated balances (MOR-754) — so a non-zero opening balance is
 * carried in, and a break in the reconciliation (missing statements) is called
 * out in the Reconciliation column.
 */
function txnRows(txns: ExportTxn[]): ExportSheet["rows"] {
  const sorted = [...txns].sort((a, b) =>
    parseSortable(a.date).localeCompare(parseSortable(b.date)),
  );
  const { byId } = computeRunningBalances(
    sorted.map((t, i) => ({
      id: String(i),
      sortableDate: parseSortable(t.date),
      amount: t.amount,
      direction: t.direction,
      balanceAfter: t.balanceAfter,
      accountKey: `${t.accountInstitution ?? ""}:${t.accountNumber ?? ""}`,
    })),
  );
  return sorted.map((t, i) => {
    const bal = byId.get(String(i));
    const gap = bal?.gap;
    return [
      parseDateCell(t.date),
      accountLabel(t.accountInstitution, t.accountNumber),
      t.description,
      t.counterpartyName || t.extractionCounterpartyName || "",
      TYPE_LABELS[t.transactionType] ?? t.transactionType ?? "",
      t.direction === "debit" ? -t.amount : "",
      t.direction === "credit" ? t.amount : "",
      bal?.runningBalance ?? "",
      gap
        ? `GAP: ${gap.difference >= 0 ? "+" : "-"}$${Math.abs(gap.difference).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unexplained before this row — statement(s) may be missing`
        : "",
    ];
  });
}

/** YYYY-MM-DD sortable form for the supported date string formats. */
function parseSortable(date: string): string {
  if (!date) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
    const [d, m, y] = date.split("/");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const match = date.match(/^(\d{1,2})\s+(\w{3,})\s+(\d{4})$/);
  if (match) {
    const mNum = (MONTHS[match[2].toLowerCase().slice(0, 3)] ?? 0) + 1;
    return `${match[3]}-${String(mNum).padStart(2, "0")}-${match[1].padStart(2, "0")}`;
  }
  return date;
}

export function buildBankTransactionsExport(
  transactions: ExportTxn[],
  accounts: ExportAccount[],
  selectedDocIds: string[],
): ExportContent {
  const docSet = selectedDocIds.length > 0 ? new Set(selectedDocIds) : null;

  // Non-duplicate transactions, scoped to the selected documents (if any).
  const txns = transactions.filter(
    (t) =>
      !t.isDuplicate &&
      (!docSet ||
        (t.sourceDocumentId != null && docSet.has(t.sourceDocumentId))),
  );

  // Summary — one row per canonical account, scoped to accounts touching the
  // selected documents. Debits negative per the agreed convention.
  const summaryAccounts = accounts.filter(
    (a) => !docSet || (a.sourceDocumentIds ?? []).some((id) => docSet.has(id)),
  );
  const summarySheet: ExportSheet = {
    name: "Accounts Summary",
    headers: [
      "Account Name",
      "Account No.",
      "Holder",
      "Type",
      "Institution",
      "Txns",
      "Total Debits",
      "Total Credits",
      "Date From",
      "Date To",
    ],
    rows: summaryAccounts.map((a) => [
      a.accountName ?? "",
      a.accountNumber ?? "",
      a.cardholderName ?? "",
      a.accountType ?? "",
      a.institution ?? "",
      a.transactionCount,
      a.totalDebits ? -a.totalDebits : 0,
      a.totalCredits ?? 0,
      parseDateCell(a.firstTxnDate),
      parseDateCell(a.lastTxnDate),
    ]),
  };

  const allSheet: ExportSheet = {
    name: "All Transactions",
    headers: TXN_HEADERS,
    rows: txnRows(txns),
  };

  // One sheet per account. Group the scoped transactions on the same canonical
  // key the Accounts Summary uses, then emit sheets in the summary's order so
  // the tabs line up with the summary rows; any transactions whose account
  // didn't match a summary row (e.g. missing account metadata) trail after.
  const byAccount = new Map<string, ExportTxn[]>();
  for (const t of txns) {
    const key = accountGroupKey(t.accountInstitution, t.accountNumber);
    const arr = byAccount.get(key) ?? [];
    arr.push(t);
    byAccount.set(key, arr);
  }

  const accountSheets: ExportSheet[] = [];
  const emitted = new Set<string>();

  for (const a of summaryAccounts) {
    const key = accountGroupKey(a.institution, a.accountNumber);
    if (emitted.has(key)) continue;
    const grp = byAccount.get(key);
    if (!grp || grp.length === 0) continue;
    emitted.add(key);
    accountSheets.push({
      name: accountSheetName(a.institution, a.accountNumber),
      headers: TXN_HEADERS,
      rows: txnRows(grp),
    });
  }

  for (const [key, grp] of byAccount) {
    if (emitted.has(key) || grp.length === 0) continue;
    emitted.add(key);
    const sample = grp[0];
    accountSheets.push({
      name: accountSheetName(sample.accountInstitution, sample.accountNumber),
      headers: TXN_HEADERS,
      rows: txnRows(grp),
    });
  }

  return {
    title: "Bank Transactions",
    sheets: [summarySheet, allSheet, ...accountSheets],
  };
}
